import { useState } from "react";
import { useLocation } from "react-router-dom";
import { Mail, Phone, MapPin, Send, Megaphone, HelpCircle, MessageSquare } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import SEO from "../components/SEO";
import axios from "axios";
import { API } from "../App";

export default function Contact() {
  const location = useLocation();
  const defaultTab = location.pathname === "/advertise" ? "advertise" : "general";
  const [generalForm, setGeneralForm] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const [advertiseForm, setAdvertiseForm] = useState({
    company_name: "",
    contact_name: "",
    email: "",
    phone: "",
    budget: "",
    placement: "",
    message: "",
  });

  const [loadingGeneral, setLoadingGeneral] = useState(false);
  const [loadingAdvertise, setLoadingAdvertise] = useState(false);

  const handleGeneralSubmit = async (e) => {
    e.preventDefault();
    
    if (!generalForm.name || !generalForm.email || !generalForm.message) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoadingGeneral(true);
    try {
      await axios.post(`${API}/contact/general`, generalForm);
      toast.success("Message sent! We'll get back to you soon.");
      setGeneralForm({ name: "", email: "", subject: "", message: "" });
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setLoadingGeneral(false);
    }
  };

  const handleAdvertiseSubmit = async (e) => {
    e.preventDefault();
    
    if (!advertiseForm.company_name || !advertiseForm.email || !advertiseForm.contact_name) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoadingAdvertise(true);
    try {
      await axios.post(`${API}/contact/advertise`, advertiseForm);
      toast.success("Advertising inquiry sent! Our team will contact you shortly.");
      setAdvertiseForm({
        company_name: "",
        contact_name: "",
        email: "",
        phone: "",
        budget: "",
        placement: "",
        message: "",
      });
    } catch (error) {
      toast.error("Failed to send inquiry. Please try again.");
    } finally {
      setLoadingAdvertise(false);
    }
  };

  return (
    <div className="min-h-screen bg-background py-12 px-4 md:px-8" data-testid="contact-page">
      <SEO 
        url="/contact"
        title="Contact Us"
        description="Get in touch with Videography Cape Town. General inquiries, advertising opportunities, and support for Cape Town's premier creative directory."
        keywords={["contact videography cape town", "advertise cape town film", "creative directory support"]}
      />
      
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="font-heading text-title uppercase">
            GET IN <span className="text-orange-500">TOUCH</span>
          </h1>
          <p className="text-zinc-500 mt-2 max-w-2xl mx-auto">
            Have a question, want to advertise, or need support? We&apos;d love to hear from you.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact Info Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-zinc-900 border border-zinc-800 p-6 space-y-6">
              <h3 className="font-heading text-xl text-white">Contact Information</h3>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Mail className="h-5 w-5 text-orange-500 mt-1" />
                  <div>
                    <p className="text-zinc-400 text-sm">Email</p>
                    <a 
                      href="mailto:info@videographycapetown.co.za" 
                      className="text-white hover:text-orange-500 transition-colors"
                    >
                      info@videographycapetown.co.za
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-orange-500 mt-1" />
                  <div>
                    <p className="text-zinc-400 text-sm">Location</p>
                    <p className="text-white">Cape Town, South Africa</p>
                  </div>
                </div>
              </div>

              <div className="pt-6 border-t border-zinc-800">
                <h4 className="font-medium text-white mb-3">Quick Links</h4>
                <ul className="space-y-2 text-sm">
                  <li>
                    <a href="/pricing" className="text-zinc-400 hover:text-orange-500 transition-colors">
                      Pricing & Plans
                    </a>
                  </li>
                  <li>
                    <a href="/categories" className="text-zinc-400 hover:text-orange-500 transition-colors">
                      Browse Categories
                    </a>
                  </li>
                  <li>
                    <a href="/signup" className="text-zinc-400 hover:text-orange-500 transition-colors">
                      Create a Profile
                    </a>
                  </li>
                </ul>
              </div>

              <div className="pt-6 border-t border-zinc-800">
                <h4 className="font-medium text-white mb-3">Business Hours</h4>
                <p className="text-zinc-400 text-sm">
                  Monday - Friday: 9:00 AM - 5:00 PM (SAST)<br />
                  Weekend: Email support only
                </p>
              </div>
            </div>
          </div>

          {/* Contact Forms */}
          <div className="lg:col-span-2">
            <Tabs defaultValue={defaultTab} className="space-y-6">
              <TabsList className="bg-zinc-900 border border-zinc-800 w-full justify-start">
                <TabsTrigger value="general" className="data-[state=active]:bg-orange-600 flex items-center gap-2">
                  <MessageSquare className="h-4 w-4" />
                  General Inquiry
                </TabsTrigger>
                <TabsTrigger value="advertise" className="data-[state=active]:bg-orange-600 flex items-center gap-2">
                  <Megaphone className="h-4 w-4" />
                  Advertise With Us
                </TabsTrigger>
              </TabsList>

              {/* General Contact Form */}
              <TabsContent value="general">
                <div className="bg-zinc-900 border border-zinc-800 p-6">
                  <h3 className="font-heading text-xl text-white mb-6">Send Us a Message</h3>
                  <form onSubmit={handleGeneralSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-zinc-300">Name *</Label>
                        <Input
                          value={generalForm.name}
                          onChange={(e) => setGeneralForm({ ...generalForm, name: e.target.value })}
                          placeholder="Your name"
                          className="mt-2 bg-zinc-800 border-zinc-700"
                          required
                        />
                      </div>
                      <div>
                        <Label className="text-zinc-300">Email *</Label>
                        <Input
                          type="email"
                          value={generalForm.email}
                          onChange={(e) => setGeneralForm({ ...generalForm, email: e.target.value })}
                          placeholder="your@email.com"
                          className="mt-2 bg-zinc-800 border-zinc-700"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <Label className="text-zinc-300">Subject</Label>
                      <Select
                        value={generalForm.subject}
                        onValueChange={(value) => setGeneralForm({ ...generalForm, subject: value })}
                      >
                        <SelectTrigger className="mt-2 bg-zinc-800 border-zinc-700">
                          <SelectValue placeholder="Select a subject" />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-700">
                          <SelectItem value="general">General Inquiry</SelectItem>
                          <SelectItem value="support">Technical Support</SelectItem>
                          <SelectItem value="billing">Billing Question</SelectItem>
                          <SelectItem value="partnership">Partnership Opportunity</SelectItem>
                          <SelectItem value="feedback">Feedback</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="text-zinc-300">Message *</Label>
                      <Textarea
                        value={generalForm.message}
                        onChange={(e) => setGeneralForm({ ...generalForm, message: e.target.value })}
                        placeholder="How can we help you?"
                        className="mt-2 min-h-32 bg-zinc-800 border-zinc-700"
                        required
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={loadingGeneral}
                      className="w-full bg-orange-600 hover:bg-orange-500"
                    >
                      {loadingGeneral ? "Sending..." : "Send Message"}
                      <Send className="h-4 w-4 ml-2" />
                    </Button>
                  </form>
                </div>
              </TabsContent>

              {/* Advertising Form */}
              <TabsContent value="advertise">
                <div className="bg-zinc-900 border border-zinc-800 p-6">
                  <h3 className="font-heading text-xl text-white mb-2">Advertise With Us</h3>
                  <p className="text-zinc-500 text-sm mb-6">
                    Reach thousands of creative professionals and clients in Cape Town&apos;s film industry.
                  </p>
                  
                  <form onSubmit={handleAdvertiseSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-zinc-300">Company Name *</Label>
                        <Input
                          value={advertiseForm.company_name}
                          onChange={(e) => setAdvertiseForm({ ...advertiseForm, company_name: e.target.value })}
                          placeholder="Your company"
                          className="mt-2 bg-zinc-800 border-zinc-700"
                          required
                        />
                      </div>
                      <div>
                        <Label className="text-zinc-300">Contact Name *</Label>
                        <Input
                          value={advertiseForm.contact_name}
                          onChange={(e) => setAdvertiseForm({ ...advertiseForm, contact_name: e.target.value })}
                          placeholder="Your name"
                          className="mt-2 bg-zinc-800 border-zinc-700"
                          required
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-zinc-300">Email *</Label>
                        <Input
                          type="email"
                          value={advertiseForm.email}
                          onChange={(e) => setAdvertiseForm({ ...advertiseForm, email: e.target.value })}
                          placeholder="your@email.com"
                          className="mt-2 bg-zinc-800 border-zinc-700"
                          required
                        />
                      </div>
                      <div>
                        <Label className="text-zinc-300">Phone</Label>
                        <Input
                          value={advertiseForm.phone}
                          onChange={(e) => setAdvertiseForm({ ...advertiseForm, phone: e.target.value })}
                          placeholder="+27 XX XXX XXXX"
                          className="mt-2 bg-zinc-800 border-zinc-700"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-zinc-300">Monthly Budget</Label>
                        <Select
                          value={advertiseForm.budget}
                          onValueChange={(value) => setAdvertiseForm({ ...advertiseForm, budget: value })}
                        >
                          <SelectTrigger className="mt-2 bg-zinc-800 border-zinc-700">
                            <SelectValue placeholder="Select budget range" />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-900 border-zinc-700">
                            <SelectItem value="under-5k">Under R5,000</SelectItem>
                            <SelectItem value="5k-10k">R5,000 - R10,000</SelectItem>
                            <SelectItem value="10k-25k">R10,000 - R25,000</SelectItem>
                            <SelectItem value="25k-50k">R25,000 - R50,000</SelectItem>
                            <SelectItem value="over-50k">Over R50,000</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-zinc-300">Ad Placement Interest</Label>
                        <Select
                          value={advertiseForm.placement}
                          onValueChange={(value) => setAdvertiseForm({ ...advertiseForm, placement: value })}
                        >
                          <SelectTrigger className="mt-2 bg-zinc-800 border-zinc-700">
                            <SelectValue placeholder="Select placement" />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-900 border-zinc-700">
                            <SelectItem value="homepage">Homepage Banner</SelectItem>
                            <SelectItem value="category">Category Pages</SelectItem>
                            <SelectItem value="search">Search Results</SelectItem>
                            <SelectItem value="sidebar">Sidebar Ads</SelectItem>
                            <SelectItem value="newsletter">Email Newsletter</SelectItem>
                            <SelectItem value="multiple">Multiple Placements</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div>
                      <Label className="text-zinc-300">Tell us about your advertising goals</Label>
                      <Textarea
                        value={advertiseForm.message}
                        onChange={(e) => setAdvertiseForm({ ...advertiseForm, message: e.target.value })}
                        placeholder="What would you like to promote? Who is your target audience?"
                        className="mt-2 min-h-32 bg-zinc-800 border-zinc-700"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={loadingAdvertise}
                      className="w-full bg-orange-600 hover:bg-orange-500"
                    >
                      {loadingAdvertise ? "Sending..." : "Submit Inquiry"}
                      <Megaphone className="h-4 w-4 ml-2" />
                    </Button>
                  </form>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
}
